import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts';

// signedOutFallback: rendered instead of the redirect to /login-registration
// when nobody is signed in, for pages that want to explain why sign-in is needed.
const ProtectedRoute = ({ children, allowAnonymous = false, signedOutFallback = null }) => {
  const { user, userProfile, loading, profileLoading, profileError, retryProfile, signOut } = useAuth();
  const location = useLocation();
  const [retrying, setRetrying] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const bypassAuth =
    typeof allowAnonymous === 'function' ? allowAnonymous(location) : Boolean(allowAnonymous);

  if (bypassAuth) return children;

  // Signed in, but the account couldn't be read (a network or database
  // blip). The session is fine — sending the coach to the login form would
  // only ask them for a password they don't need. Sign out stays on offer for
  // someone who wants a different account, or whose account won't load.
  // Checked before the loading screen: a retry keeps the error set and loads
  // again, and the card (with "Trying again…") stays up for it.
  if (user && !userProfile && profileError) {
    const handleRetry = async () => {
      setRetrying(true);
      try {
        await retryProfile?.();
      } finally {
        setRetrying(false);
      }
    };
    const handleSignOut = async () => {
      setSigningOut(true);
      try {
        await signOut?.();
      } finally {
        setSigningOut(false);
      }
    };
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-sm text-center space-y-3">
          <p className="text-foreground font-medium">We couldn&apos;t load your account.</p>
          <p className="text-sm text-muted-foreground">
            You&apos;re still signed in. Check your connection and try again.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying || profileLoading || signingOut}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
            >
              {retrying || profileLoading ? 'Trying again…' : 'Try again'}
            </button>
            <button
              type="button"
              onClick={handleSignOut}
              disabled={retrying || signingOut}
              className="inline-flex items-center justify-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground disabled:opacity-60"
            >
              {signingOut ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Signed in, account still loading: wait rather than read as signed out.
  if (loading || (user && !userProfile && profileLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Checking your session...</p>
      </div>
    );
  }

  if (!user && signedOutFallback) return signedOutFallback;

  if (!user || !userProfile) {
    return <Navigate to="/login-registration" replace state={{ from: location }} />;
  }

  return children;
};

export default ProtectedRoute;
